/**
 * Ubicación completa de un registro, en texto en la card
 * (`RecordLotIdentity`). Del dato más específico al más general, sin
 * repetir y sin tope de segmentos:
 *   dirección (`address` | `street_name`/`street` + `street_number`, UF primero)
 *   · barrio · localidad/municipio · departamento · provincia.
 * Parcelas CAD (`getmycadparcels`): no traen ubicación textual → vacío.
 * @see docs/frontend/commercial-summary.md
 */
const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());
const first = (...vals) => vals.map(clean).find(Boolean) || '';

const streetOf = (src) => {
  if (!src) return '';
  const address = clean(src.address);
  if (address) return address;
  const street = first(src.street_name, src.street);
  if (!street) return '';
  const number = clean(src.street_number);
  return number ? `${street} ${number}` : street;
};

/** @returns {string[]} segmentos (vacío si no hay datos). */
export function buildRecordLocationParts(lot, unit = null) {
  const candidates = [
    streetOf(unit) || streetOf(lot),
    first(unit?.neighborhood_name, unit?.neighborhood, lot?.neighborhood_name, lot?.neighborhood),
    first(unit?.locality_name, unit?.municipality_name, lot?.locality_name, lot?.municipality_name),
    first(unit?.department_name, lot?.department_name),
    first(unit?.province_name, lot?.province_name),
  ];
  const seen = new Set();
  return candidates.filter((c) => {
    if (!c) return false;
    const key = c.toLocaleLowerCase('es');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** 'Av. Colón 123 · Capital · Córdoba' o '' si no hay datos. */
export function buildRecordLocationLabel(lot, unit = null) {
  return buildRecordLocationParts(lot, unit).join(' · ');
}

/**
 * Separa la calle del resto. La calle, si existe, es el título de la card;
 * el resto queda como línea de lugar.
 * @returns {{ street: string, place: string }}
 */
export function splitRecordLocation(lot, unit = null) {
  const parts = buildRecordLocationParts(lot, unit);
  const street = streetOf(unit) || streetOf(lot);
  const streetKey = street.toLocaleLowerCase('es');
  if (street && parts[0] && parts[0].toLocaleLowerCase('es') === streetKey) {
    return { street: parts[0], place: parts.slice(1).join(' · ') };
  }
  return { street: '', place: parts.join(' · ') };
}

/**
 * Ubicación de la UF solo cuando aporta algo que el lote no dice.
 * Si repite la del lote, la fila no la muestra.
 */
export function unitLocationDistinctFromLot(lot, unit) {
  const unitLabel = buildRecordLocationLabel(lot, unit);
  if (!unitLabel) return '';
  return unitLabel === buildRecordLocationLabel(lot) ? '' : unitLabel;
}
